import { byPrefixAndName } from '@awesome.me/kit-5c288c4b3f/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { ReactNode } from 'react'
import {
  Checkbox as AriaCheckbox,
  CheckboxGroup as AriaCheckboxGroup,
  CheckboxGroupProps as AriaCheckboxGroupProps,
  CheckboxProps,
  ValidationResult,
  composeRenderProps,
} from 'react-aria-components'
import { tv } from 'tailwind-variants'
import { composeTailwindRenderProps, focusRing } from '../../utils'
import { Description, FieldError, Label } from './Field'

export interface CheckboxGroupProps
  extends Omit<AriaCheckboxGroupProps, 'children'> {
  label?: string
  children?: ReactNode
  description?: string
  errorMessage?: string | ((validation: ValidationResult) => string)
}

export function CheckboxGroup(props: CheckboxGroupProps) {
  return (
    <AriaCheckboxGroup
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        'flex flex-col gap-2'
      )}
    >
      <Label>{props.label}</Label>
      {props.children}
      {props.description && <Description>{props.description}</Description>}
      <FieldError>{props.errorMessage}</FieldError>
    </AriaCheckboxGroup>
  )
}

const checkboxStyles = tv({
  base: 'flex gap-2 items-center group text-sm transition',
  variants: {
    isDisabled: {
      false: 'text-default-50',
      true: 'text-default-500',
    },
  },
})

const boxStyles = tv({
  extend: focusRing,
  base: 'w-5 h-5 flex-shrink-0 rounded-md flex items-center justify-center border-2 transition',
  variants: {
    isSelected: {
      false:
        'bg-default-1000  border-default-500  group-pressed:border-default-600 ',
      true: 'bg-primary border-primary group-pressed:bg-primary-dark group-pressed:border-primary-dark  ',
    },
    isInvalid: {
      true: 'bg-danger border-danger group-pressed:bg-danger-dark group-pressed:border-danger-dark',
    },
    isDisabled: {
      true: 'bg-default-500 border-default-500  ',
    },
  },
})

const iconStyles = 'w-4 h-4 text-default-50 group-disabled:text-default-400 '

export function Checkbox(props: CheckboxProps) {
  return (
    <AriaCheckbox
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        checkboxStyles({ ...renderProps, className })
      )}
    >
      {({ isSelected, isIndeterminate, ...renderProps }) => (
        <>
          <div
            className={boxStyles({
              isSelected: isSelected || isIndeterminate,
              ...renderProps,
            })}
          >
            {isIndeterminate ? (
              <FontAwesomeIcon
                icon={byPrefixAndName.fal['minus']}
                aria-hidden
                className={iconStyles}
              />
            ) : isSelected ? (
              <FontAwesomeIcon
                icon={byPrefixAndName.fal['check']}
                aria-hidden
                className={iconStyles}
              />
            ) : null}
          </div>
          {props.children}
        </>
      )}
    </AriaCheckbox>
  )
}
